import { useAuth } from 'layout/context/auth-context'
import { useLoading } from 'layout/context/loading-context'
import { Card } from 'primereact/card'
import { DataView } from 'primereact/dataview'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import CategoryBreakdownChips from 'shared/components/category-breakdown-chips'
import { DateFilterState } from 'shared/date-filter/date-filter-component'
import { getBrazilDate } from 'shared/utils/get-brazil-date'
import EventImageViewer from '../../anpr-events/components/anpr-event-image-dialog'
import { useAHCREvent } from '../hooks/useAHCREvent'
import { AHCREvent, emptyAHCREvent } from '../types/ahcr-event.types'
import { buildAhcrAttributeBreakdown } from '../utils/attribute-breakdown'
import { filterAHCREvent } from '../utils/ahcr-event-filter'
import { exportAHCREventsToCSV } from '../utils/export-csv'
import AHCREventDetailViewer from './ahcr-event-detail-dialog'
import AHCREventGridItem from './ahcr-event-grid-item'
import AHCREventListItem from './ahcr-event-list-item'
import AHCREventsFilters from './ahcr-event-filters'
import './ahcr-event-view.scss'

const blankDateFilter: DateFilterState = {
  preset: 'today',
  endDate: getBrazilDate('end'),
  startDate: getBrazilDate('start'),
}

const AHCREventsView = () => {
  const [searchParams] = useSearchParams()
  const [dateFilter, setDateFilter] = useState<DateFilterState>(blankDateFilter)
  const [datePreset, setDatePreset] = useState<string>('today')
  const [ahcrEvent, setAHCREvent] = useState<AHCREvent>(emptyAHCREvent)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [ahcrEventDialog, setAHCREventDialog] = useState(false)
  const [genderFilter, setGenderFilter] = useState<string>('')
  const [hatFilter, setHatFilter] = useState<string>('')
  const [bagFilter, setBagFilter] = useState<string>('')
  const [maskFilter, setMaskFilter] = useState<string>('')
  const [glassFilter, setGlassFilter] = useState<string>('')
  const [thingsFilter, setThingsFilter] = useState<string>('')
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>('')
  const [hairStyleFilter, setHairStyleFilter] = useState<string>('')
  const [jacketTypeFilter, setJacketTypeFilter] = useState<string>('')
  const [jacketColorFilter, setJacketColorFilter] = useState<string>('')
  const [trousersTypeFilter, setTrousersTypeFilter] = useState<string>('')
  const [trousersColorFilter, setTrousersColorFilter] = useState<string>('')
  // Pré-preenchido por quem chega aqui via "Ver eventos" no mapa (?camera=...).
  const [cameraNameFilter, setCameraNameFilter] = useState<string>(
    () => searchParams.get('camera') ?? ''
  )
  const [layout, setLayout] = useState<'grid' | 'list'>('grid')
  const [searchButtonHighlight, setSearchButtonHighlight] = useState(false)

  const { isLoading } = useLoading()
  const { ahcrEvents, fetchAHCREvents } = useAHCREvent()
  const { clientId } = useAuth()

  const applyDatePreset = (preset: string) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (preset) {
      case 'today':
        setDateFilter({ startDate: new Date(today.setHours(0, 0, 0, 0)), endDate: new Date(now.setHours(23, 59, 59, 999)), preset: 'today' })
        break
      case 'yesterday': {
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        setDateFilter({ startDate: new Date(yesterday.setHours(0, 0, 0, 0)), endDate: new Date(yesterday.setHours(23, 59, 59, 999)), preset: 'yesterday' })
        break
      }
      case 'last7days': {
        const last7 = new Date(today)
        last7.setDate(last7.getDate() - 7)
        setDateFilter({ startDate: new Date(last7.setHours(0, 0, 0, 0)), endDate: new Date(now.setHours(23, 59, 59, 999)), preset: 'week' })
        break
      }
      case 'last30days': {
        const last15 = new Date(today)
        last15.setDate(last15.getDate() - 15)
        setDateFilter({ startDate: new Date(last15.setHours(0, 0, 0, 0)), endDate: new Date(now.setHours(23, 59, 59, 999)), preset: '2 weeks' })
        break
      }
      case 'custom':
        setDateFilter((prev) => ({ ...prev, preset: 'custom' }))
        break
    }
    setDatePreset(preset)
    setSearchButtonHighlight(true)
  }

  const handleSearch = () => {
    if (clientId) {
      fetchAHCREvents({ clientId, startDate: dateFilter.startDate, endDate: dateFilter.endDate })
      setSearchButtonHighlight(false)
    }
  }

  const handleDateFilterChange = (prop: 'startDate' | 'endDate', value: any) => {
    setDateFilter((prev) => ({ ...prev, [prop]: new Date(value) }))
    setSearchButtonHighlight(true)
  }

  useEffect(() => {
    if (clientId) {
      fetchAHCREvents({ clientId, startDate: dateFilter.startDate, endDate: dateFilter.endDate })
    }
  }, [clientId, fetchAHCREvents])

  const uniqueCameraNames = useMemo(
    () =>
      Array.from(new Set(ahcrEvents.map((e) => e.cameraName).filter(Boolean))).map((name) => ({
        label: name,
        value: name,
      })),
    [ahcrEvents]
  )

  const hasAHCRData = useMemo(() => {
    const eventsToCheck = cameraNameFilter ? ahcrEvents.filter((e) => e.cameraName === cameraNameFilter) : ahcrEvents
    return eventsToCheck.some((e) => e.gender !== null)
  }, [ahcrEvents, cameraNameFilter])

  useEffect(() => {
    if (!hasAHCRData) {
      setGenderFilter('')
      setHatFilter('')
      setBagFilter('')
      setMaskFilter('')
      setGlassFilter('')
      setThingsFilter('')
      setAgeGroupFilter('')
      setHairStyleFilter('')
      setJacketTypeFilter('')
      setJacketColorFilter('')
      setTrousersTypeFilter('')
      setTrousersColorFilter('')
    }
  }, [hasAHCRData])

  const filteredEvents = useMemo(
    () =>
      ahcrEvents.filter((event) =>
        filterAHCREvent(event, {
          cameraNameFilter,
          genderFilter,
          hatFilter,
          bagFilter,
          maskFilter,
          glassFilter,
          thingsFilter,
          ageGroupFilter,
          hairStyleFilter,
          jacketTypeFilter,
          jacketColorFilter,
          trousersTypeFilter,
          trousersColorFilter,
        })
      ),
    [
      ahcrEvents,
      cameraNameFilter,
      genderFilter,
      hatFilter,
      bagFilter,
      maskFilter,
      glassFilter,
      thingsFilter,
      ageGroupFilter,
      hairStyleFilter,
      jacketTypeFilter,
      jacketColorFilter,
      trousersTypeFilter,
      trousersColorFilter,
    ]
  )

  const viewEventDetails = (event: AHCREvent) => {
    setAHCREvent({ ...event })
    setAHCREventDialog(true)
  }

  const exportEvents = () => exportAHCREventsToCSV(filteredEvents)

  const attributeBreakdown = useMemo(
    () => buildAhcrAttributeBreakdown(filteredEvents),
    [filteredEvents]
  )

  const itemTemplate = (event: AHCREvent) => {
    if (!event) return null
    if (layout === 'grid') {
      return <AHCREventGridItem event={event} onViewDetails={viewEventDetails} onImageClick={setSelectedImage} />
    }
    return <AHCREventListItem event={event} onViewDetails={viewEventDetails} onImageClick={setSelectedImage} />
  }

  return (
    <div className='events-container'>
      <div className='events-header'>
        <div className='events-title-section'>
          <i className='pi pi-eye events-main-icon' />
          <h1 className='events-title'>Monitoramento de Pessoas</h1>
        </div>
        <div className='events-subtitle'>Eventos de classificação e reconhecimento humano em tempo real</div>
      </div>

      <Card className='events-card'>
        <div className='events-toolbar'>
          <AHCREventsFilters
            dateFilter={dateFilter}
            datePreset={datePreset}
            searchButtonHighlight={searchButtonHighlight}
            isLoading={isLoading}
            clientId={clientId}
            filteredEventsCount={filteredEvents.length}
            cameraNameFilter={cameraNameFilter}
            genderFilter={genderFilter}
            hatFilter={hatFilter}
            bagFilter={bagFilter}
            maskFilter={maskFilter}
            glassFilter={glassFilter}
            thingsFilter={thingsFilter}
            ageGroupFilter={ageGroupFilter}
            hairStyleFilter={hairStyleFilter}
            jacketTypeFilter={jacketTypeFilter}
            jacketColorFilter={jacketColorFilter}
            trousersTypeFilter={trousersTypeFilter}
            trousersColorFilter={trousersColorFilter}
            uniqueCameraNames={uniqueCameraNames}
            hasAHCRData={hasAHCRData}
            layout={layout}
            onExport={exportEvents}
            onApplyDatePreset={applyDatePreset}
            onSearch={handleSearch}
            onDateFilterChange={handleDateFilterChange}
            onCameraNameFilterChange={setCameraNameFilter}
            onGenderFilterChange={setGenderFilter}
            onHatFilterChange={setHatFilter}
            onBagFilterChange={setBagFilter}
            onMaskFilterChange={setMaskFilter}
            onGlassFilterChange={setGlassFilter}
            onThingsFilterChange={setThingsFilter}
            onAgeGroupFilterChange={setAgeGroupFilter}
            onHairStyleFilterChange={setHairStyleFilter}
            onJacketTypeFilterChange={setJacketTypeFilter}
            onJacketColorFilterChange={setJacketColorFilter}
            onTrousersTypeFilterChange={setTrousersTypeFilter}
            onTrousersColorFilterChange={setTrousersColorFilter}
            onLayoutChange={setLayout}
          />
        </div>

        <CategoryBreakdownChips entries={attributeBreakdown} />

        <DataView
          value={filteredEvents}
          layout={layout}
          itemTemplate={itemTemplate}
          paginator
          rows={12}
          loading={isLoading}
          emptyMessage='Nenhum evento AHCR encontrado para este cliente.'
          paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          currentPageReportTemplate='Mostrando {first} a {last} de {totalRecords} eventos'
          rowsPerPageOptions={[12, 24, 36, 48]}
        />

        <AHCREventDetailViewer
          setSelectedImage={setSelectedImage}
          AHCREvent={ahcrEvent}
          hideDialog={() => setAHCREventDialog(false)}
          visible={ahcrEventDialog}
        />
        <EventImageViewer selectedImage={selectedImage} setSelectedImage={setSelectedImage} />
      </Card>
    </div>
  )
}

export default AHCREventsView
